import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, AppBar, Toolbar, IconButton, Typography, Snackbar, Alert, Tooltip, Button, Menu, MenuItem, ListItemIcon, ListItemText, Switch } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import SettingsIcon from '@mui/icons-material/Settings';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import Sidebar from './Sidebar';
import { toggleTheme, toggleLayoutWidth } from '../../store/themeSlice';
import DataUiDebugger from '../DataUiDebugger';

const Layout = ({ children, onExport, onImport, onAddSession, sidebarOpen: controlledSidebarOpen, onToggleSidebar }) => {
  // Redux/Router hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const themeMode = useSelector((state) => state.theme.mode);
  const layoutWidth = useSelector((state) => state.theme.layoutWidth);
  const toast = useSelector((state) => state.competition.toast);
  
  // State hooks
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [internalSidebarOpen, setInternalSidebarOpen] = useState(false);

  // Support both controlled and uncontrolled sidebar state
  const isControlled = controlledSidebarOpen !== undefined;
  const sidebarOpen = isControlled ? controlledSidebarOpen : internalSidebarOpen;
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isConnected, setIsConnected] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [useRemoteDb, setUseRemoteDb] = useState(false);
  const [isDevMode] = useState(process.env.NODE_ENV === 'development');
  
  // Check if remote database is configured
  const remoteUrl = process.env.REACT_APP_SUPABASE_REMOTE_URL;
  const remoteKey = process.env.REACT_APP_SUPABASE_REMOTE_KEY;
  const isRemoteConfigured = !!(remoteUrl && remoteKey);

  // Initialize database preference from localStorage
  useEffect(() => {
    const savedPref = localStorage.getItem('virtius-db-preference');
    if (savedPref) {
      setUseRemoteDb(savedPref === 'remote');
    }
  }, []);

  // Monitor database connection
  useEffect(() => {
    const checkConnection = async () => {
      try {
        setIsLoading(true);
        // Simple health check by attempting to access environment
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
        if (supabaseUrl) {
          // Try to fetch from the Supabase instance
          const response = await fetch(supabaseUrl, { 
            method: 'HEAD',
            mode: 'no-cors' 
          });
          setIsConnected(true);
        } else {
          setIsConnected(false);
        }
      } catch (error) {
        setIsConnected(false);
      } finally {
        setIsLoading(false);
      }
    };

    // Check immediately and then periodically
    checkConnection();
    const interval = setInterval(checkConnection, 30000); // Check every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  // Effect hooks
  React.useEffect(() => {
    if (toast?.message) {
      setSnackbarMessage(toast.message);
      setSnackbarSeverity(toast.severity || 'success');
      setSnackbarOpen(true);
    } else {
      // Clear snackbar when toast is null
      setSnackbarOpen(false);
      setSnackbarMessage('');
    }
  }, [toast]);

  // Handle database toggle
  const handleDbToggle = () => {
    // If trying to switch to remote but not configured, show warning
    if (!useRemoteDb && !isRemoteConfigured) {
      alert('Remote database not configured.\n\nAdd REACT_APP_SUPABASE_REMOTE_URL and REACT_APP_SUPABASE_REMOTE_KEY to your environment to enable remote database toggle.');
      return;
    }

    const newValue = !useRemoteDb;
    const targetDb = newValue ? 'remote' : 'local';
    
    // Single confirmation with warning for remote
    const message = newValue
      ? '⚠️ Switch to REMOTE database?\n\nYou will be working with production data.\nBe careful with any changes!\n\nPage will refresh automatically.'
      : 'Switch to LOCAL database?\n\nPage will refresh automatically.';
    
    if (!window.confirm(message)) {
      return;
    }
    
    setUseRemoteDb(newValue);
    localStorage.setItem('virtius-db-preference', targetDb);
    window.location.reload();
  };

  // Handler functions
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    dispatch({ type: 'competition/clearToast' });
  };

  const handleDrawerToggle = () => {
    if (isControlled && onToggleSidebar) {
      onToggleSidebar();
    } else {
      setInternalSidebarOpen(!internalSidebarOpen);
    }
  };

  const handleSettingsOpen = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        data-ui="nav-appbar"
        sx={{
          background: theme => theme.palette.mode === 'dark'
            ? 'linear-gradient(90deg, #2a2b2e 0%, #333437 50%, #2a2b2e 100%)'
            : 'linear-gradient(90deg, #f5f5f5 0%, #ffffff 50%, #f5f5f5 100%)',
          color: 'text.primary',
          boxShadow: 1
        }}
      >
        <Toolbar>
          <IconButton
            data-ui="nav-sidebar-toggle"
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            data-ui="nav-logo"
            sx={{
              flexGrow: 1,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'flex-start',
              '&:hover': {
                opacity: 0.8
              }
            }}
            onClick={() => navigate('/')}
          >
            <img
              src="/assets/images/virtius_logo.svg"
              alt="Virtius"
              style={{
                height: '32px',
                width: 'auto'
              }}
            />
            <img
              src="/assets/images/nextgen_logo.svg"
              alt="NextGen"
              style={{
                height: '32px',
                width: 'auto',
                marginLeft: '8px',
                marginTop: '-8px'
              }}
            />
          </Box>
          
          {/* Database Toggle Button - Only show in dev mode */}
          {isDevMode && (
            <Tooltip
              title={isRemoteConfigured
                ? `Click to switch to ${useRemoteDb ? 'LOCAL' : 'REMOTE'} database`
                : "Remote database not configured - add REACT_APP_SUPABASE_REMOTE_* env vars"}
              arrow
            >
              <Box
                data-ui="nav-db-toggle"
                onClick={handleDbToggle}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mr: 2,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: '6px',
                  border: '1.5px solid',
                  cursor: isRemoteConfigured ? 'pointer' : 'help',
                  opacity: isRemoteConfigured ? 1 : 0.6,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': isRemoteConfigured ? {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  } : {},
                  bgcolor: isRemoteConfigured 
                    ? useRemoteDb
                      ? themeMode === 'dark' ? 'rgba(168, 85, 247, 0.15)' : 'rgba(168, 85, 247, 0.08)'
                      : themeMode === 'dark' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.08)'
                    : themeMode === 'dark' ? 'rgba(107, 114, 128, 0.1)' : 'rgba(107, 114, 128, 0.05)',
                  borderColor: isRemoteConfigured 
                    ? useRemoteDb
                      ? themeMode === 'dark' ? '#a855f7' : '#d8b4fe'
                      : themeMode === 'dark' ? '#3b82f6' : '#60a5fa'
                    : themeMode === 'dark' ? '#6b7280' : '#d1d5db',
                }}
              >
                {/* Status Dot */}
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: isLoading ? '#eab308' : isConnected ? '#22c55e' : '#ef4444',
                    flexShrink: 0,
                    animation: isLoading ? 'pulse 1.5s ease-in-out infinite' : 'none',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                    },
                  }}
                />
                {/* Label */}
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                    color: useRemoteDb 
                      ? themeMode === 'dark' ? '#d8b4fe' : '#a855f7'
                      : themeMode === 'dark' ? '#93c5fd' : '#3b82f6',
                  }}
                >
                  {useRemoteDb ? 'REMOTE' : 'LOCAL'}
                </Typography>
                {!isRemoteConfigured && <span style={{ fontSize: '0.7rem', marginLeft: '2px' }}>🔒</span>}
              </Box>
            </Tooltip>
          )}
          
          <IconButton
            data-ui="nav-settings-toggle"
            color="inherit"
            onClick={handleSettingsOpen}
            sx={{ ml: 1 }}
          >
            <SettingsIcon />
          </IconButton>
          <Menu
            data-ui="nav-settings-menu"
            anchorEl={settingsAnchorEl}
            open={Boolean(settingsAnchorEl)}
            onClose={handleSettingsClose}
            PaperProps={{
              sx: { width: 240, maxWidth: '100%' }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem
              data-ui="nav-settings-dark-mode"
              onClick={() => { dispatch(toggleTheme()); handleSettingsClose(); }}
            >
              <ListItemIcon>
                {themeMode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </ListItemIcon>
              <ListItemText primary="Dark Mode" />
              <Switch
                edge="end"
                checked={themeMode === 'dark'}
                inputProps={{ 'aria-label': 'dark mode toggle', 'data-ui': 'nav-settings-dark-mode-switch' }}
              />
            </MenuItem>
            <MenuItem
              data-ui="nav-settings-full-width"
              onClick={() => { dispatch(toggleLayoutWidth()); handleSettingsClose(); }}
            >
              <ListItemIcon>
                <AspectRatioIcon />
              </ListItemIcon>
              <ListItemText primary="Full Width" />
              <Switch
                edge="end"
                checked={layoutWidth === 'full'}
                inputProps={{ 'aria-label': 'full width toggle', 'data-ui': 'nav-settings-full-width-switch' }}
              />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Sidebar
        open={sidebarOpen}
        onClose={handleDrawerToggle}
        onExport={onExport}
        onImportClick={onImport}
        onAddSession={onAddSession}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          mt: 4,
          p: 0,
        }}
      >
        {children}
      </Box>
      <Snackbar
        data-ui="feedback-snackbar"
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          data-ui={`feedback-${snackbarSeverity}-alert`}
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
          action={
            toast?.undoAction ? (
              <Button
                data-ui="btn-undo"
                color="inherit"
                size="small"
                onClick={() => {
                  toast.undoAction();
                  // Add small delay to allow toast animation to complete
                  setTimeout(() => {
                    handleSnackbarClose();
                  }, 100);
                }}
              >
                UNDO
              </Button>
            ) : null
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <DataUiDebugger />
    </Box>
  );
};

export default Layout;
